import { forwardRef, ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { ArrowRightIcon } from '../icons';

const variants = {
  primary:
    'bg-gradient-to-r from-brand to-brand-600 hover:from-brand-400 hover:to-brand text-white shadow-2xl shadow-blue-500/30 hover:shadow-blue-500/50',
  secondary:
    'bg-white/10 backdrop-blur-md hover:bg-white/20 text-white border border-white/30 hover:border-white/50',
  cta: 'bg-[#4f6cff] hover:bg-blue-600 text-white shadow-2xl shadow-blue-500/40',
  ghost: 'bg-transparent hover:bg-white/10 text-white border border-white/20 hover:border-white/30',
  outline:
    'bg-transparent hover:bg-gray-50 text-gray-700 border border-gray-200 hover:border-gray-300',
} as const;

const sizes = {
  sm: 'px-4 py-2 text-sm gap-2',
  md: 'px-6 py-3 text-base gap-2',
  lg: 'px-10 py-5 text-lg gap-3',
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  showArrow?: boolean;
  isRTL?: boolean;
  loading?: boolean;
  children: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      showArrow = false,
      isRTL = false,
      loading = false,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'group relative inline-flex items-center justify-center rounded-full font-bold transition-all duration-300 overflow-hidden',
          'hover:scale-105 hover:-translate-y-0.5',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:translate-y-0',
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {/* Shimmer effect */}
        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        <span className="relative">{loading ? '...' : children}</span>
        {showArrow && !loading && (
          <ArrowRightIcon
            className={cn(
              'relative w-5 h-5 transition-transform',
              isRTL ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'
            )}
          />
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

// Link variant of the button for navigation
export interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  showArrow?: boolean;
  isRTL?: boolean;
  to?: string;
  children: ReactNode;
}

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      showArrow = false,
      isRTL = false,
      to,
      href,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const classes = cn(
      'group relative inline-flex items-center justify-center rounded-full font-bold transition-all duration-300 overflow-hidden',
      'hover:scale-105 hover:-translate-y-0.5',
      variants[variant],
      sizes[size],
      className
    );

    const content = (
      <>
        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        <span className="relative">{children}</span>
        {showArrow && (
          <ArrowRightIcon
            className={cn(
              'relative w-5 h-5 transition-transform',
              isRTL ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'
            )}
          />
        )}
      </>
    );

    if (to) {
      return (
        <Link to={to} className={classes} {...(props as object)}>
          {content}
        </Link>
      );
    }

    return (
      <a ref={ref} href={href} className={classes} {...props}>
        {content}
      </a>
    );
  }
);

ButtonLink.displayName = 'ButtonLink';

